import type { TaskProjectDto } from '../../api/tasks';
import type { TeamDto, TeamMemberDto } from '../../api/project-teams';
import { Avatar } from '../../components/shared';
import { tProject } from '../../lib/project-i18n';
import './SpaceInfoHoverCard.css';

/**
 * SpaceInfoHoverCard (2026-09-14)
 *
 * 스페이스 상세 화면 우상단 정보(i) 버튼에 마우스를 올리면(키보드 포커스 포함) 뜨는 카드.
 *
 * - 오른쪽 **스페이스 정보** 패널이 닫혀 있을 때: 패널을 다시 열지 않고도 같은 정보를
 *   (소유자·설명·멤버·프로젝트 진행/완료) 바로 훑어볼 수 있게 요약 카드를 띄운다.
 * - 패널이 열려 있을 때: 같은 내용이 옆에 이미 보이므로 카드 대신 버튼 이름만 짧게 띄운다.
 *
 * 기존 패널(SpaceDetailPage 의 `.spd-info`) 렌더 경로는 건드리지 않으려고 신규 모듈로 뺐다.
 * 집계 규칙은 패널과 같다 — 태스크가 있고 전부 완료된 프로젝트만 '완료'.
 */
export interface SpaceInfoHoverCardProps {
  id: string;
  /** 오른쪽 패널이 열려 있으면 요약 카드 대신 이름 툴팁만 보인다. */
  compact: boolean;
  team: TeamDto | null;
  members: TeamMemberDto[];
  projects: TaskProjectDto[];
  myId: string;
}

export function SpaceInfoHoverCard({ id, compact, team, members, projects, myId }: SpaceInfoHoverCardProps) {
  if (compact) {
    return (
      <span id={id} role="tooltip" className="spd-hover spd-hover--label">
        {tProject('project.space.info')}
      </span>
    );
  }

  const owner = members.find((m) => m.role === 'LEADER') ?? members[0] ?? null;
  const done = projects.filter((p) => (p.taskCount ?? 0) > 0 && (p.doneTaskCount ?? 0) >= (p.taskCount ?? 0)).length;
  const active = projects.length - done;
  const pct = projects.length > 0 ? Math.round((done / projects.length) * 100) : 0;

  return (
    <div id={id} role="tooltip" className="spd-hover spd-hover--card">
      <div className="spd-hover-head">
        <span className="spd-hover-kicker">{tProject('project.space.info')}</span>
        <b className="spd-hover-name">
          {/* ✅ 2026-10-08 (저녁) — ★ 되살렸다. 서버가 `color` 를 실어 준다(TeamDto.color 칸 참조).
              ⇒ ⚠ `null` 일 수 있고 ⇒ 그때는 ⭐ 점을 «안 그린다» — 정본 그대로다.
                ⇒ ★ 여기서 `stripColor` 폴백을 쓰지 «않는» 까닭: 이 카드는 ⭐ 요약이라
                  ⇒ ★ 「고른 색이 있을 때만」 점으로 알린다(⛔ 해시 색을 여기 또 그리면 소음이다). */}
          {team?.color && <i className="spd-hover-dot" style={{ background: team.color }} aria-hidden />}
          {team?.name ?? tProject('project.space.loading')}
        </b>
      </div>

      <div className="spd-hover-row">
        {owner ? (
          <>
            <Avatar userId={Number(owner.userId) || 0} name={owner.name} src={owner.avatarUrl} size="xs" />
            <span className="spd-hover-owner">
              {owner.name}{owner.userId === myId ? ` · ${tProject('project.space.me')}` : ''}
            </span>
            <span className="spd-hover-role">{tProject('project.space.role_owner')}</span>
          </>
        ) : (
          <span className="spd-hover-muted">{tProject('project.info.no_members')}</span>
        )}
      </div>

      <div className="spd-hover-field">
        <span className="spd-hover-label">{tProject('project.info.desc')}</span>
        <p className={team?.description ? 'spd-hover-desc' : 'spd-hover-desc spd-hover-muted'}>
          {team?.description || tProject('project.space.desc_placeholder')}
        </p>
      </div>

      <div className="spd-hover-field">
        <span className="spd-hover-label">{tProject('project.space.members', { n: String(members.length) })}</span>
        <span className="spd-avatars">
          {members.slice(0, 8).map((m) => (
            <Avatar key={m.userId} userId={Number(m.userId) || 0} name={m.name} src={m.avatarUrl} size="xs" />
          ))}
          {members.length === 0 && <span className="spd-hover-muted">{tProject('project.info.no_members')}</span>}
        </span>
      </div>

      <div className="spd-hover-field">
        <span className="spd-hover-label">
          {tProject('project.space.chart')} · {projects.length} {tProject('project.space.chart_unit')}
        </span>
        <span
          className="spd-hover-bar"
          style={{ ['--pct' as string]: `${pct}` }}
          role="img"
          aria-label={tProject('project.space.chart_aria', { done: String(done), total: String(projects.length) })}
        />
        <span className="spd-hover-legend">
          <span><i className="spd-legend-dot spd-legend-dot--active" aria-hidden />{tProject('project.space.status_active')} {active}</span>
          <span><i className="spd-legend-dot spd-legend-dot--done" aria-hidden />{tProject('project.space.status_done')} {done}</span>
        </span>
      </div>
    </div>
  );
}
